import React, { useState } from 'react';
import { useScrollReveal } from '../../../hooks/useScrollReveal';

const PROCESS_STEPS = [
  { step: 1, title: 'Get Displayer License', desc: 'Register as Individual (up to 10 devices in one ULB) or Professional (unlimited, any ULB).', icon: 'ri-user-star-line' },
  { step: 2, title: 'Apply for Device Permission', desc: 'Submit device plan — type, size, location. Separate permission per device.', icon: 'ri-file-list-3-line' },
  { step: 3, title: 'Structural Certificate', desc: 'Required for 50+ sq.ft devices & all unipoles. Certified by empaneled engineer.', icon: 'ri-shield-check-line' },
  { step: 4, title: 'Insurance & Deposit', desc: 'Public Liability Insurance + security deposit (₹25K–₹1L).', icon: 'ri-safe-2-line' },
  { step: 5, title: 'Permission Granted', desc: 'Commissioner approves. Valid 3 years (permanent) or 30 days (temporary).', icon: 'ri-checkbox-circle-line' },
  { step: 6, title: 'Install & Display', desc: 'Build, undergo ULB inspection, then start displaying advertisements.', icon: 'ri-tv-2-line' },
];

const DEVICE_TYPES = [
  { type: 'Permanent', examples: 'Hoardings, Unipoles, Glow signs, Digital displays, Kiosks', license: true, structural: 'If 50+ sq.ft', insurance: true, validity: '3 years' },
  { type: 'Temporary', examples: 'Flex banners, Cloth banners, Flags, Balloons', license: false, structural: 'No', insurance: false, validity: '30 days' },
  { type: 'Digital', examples: 'LED screens, LCD displays, Electronic building wraps', license: true, structural: 'If 50+ sq.ft', insurance: true, validity: '3 years' },
  { type: 'Mobile', examples: 'Ads on buses, autos, taxis, ad vehicles', license: true, structural: 'No', insurance: false, validity: '3 years' },
];

const FAQS = [
  { q: 'Is a license needed for my own shop name board?', a: 'No. Your own business name on your own building is exempt under Rule 11(e). However, advertising a third-party product requires permission.' },
  { q: "What's the difference between License and Permission?", a: 'License registers YOU as a displayer (one-time per 3 years). Permission approves EACH specific device — its type, size, and location.' },
  { q: 'What if I already have a device from before these rules?', a: 'Apply under Rule 14 — simplified process but still requires License + Permission + Structural Certificate.' },
  { q: 'What happens if I put up an ad without permission?', a: 'Penalties: ₹10,000 (1st offense), ₹20,000 (2nd), ₹50,000 or license cancellation (3rd). Unauthorized hoardings: ₹20,000–30,000/day.' },
  { q: 'Can unauthorized devices be regularized?', a: 'Yes — pay 200% of normal permission fee plus GST. Competent authority decides case-by-case.' },
  { q: 'What are the renewal timelines?', a: '30+ days before expiry: no penalty. Within 30 days before: +25%. Within 30 days after: +50%. Beyond: terminated, +100%.' },
  { q: 'What sizes are allowed?', a: 'Min 10ft×10ft (100 sq.ft), Max 30ft×40ft (1,200 sq.ft). Large: 601–1200, Medium: 301–600, Small: up to 300 sq.ft.' },
  { q: 'Where are display devices prohibited?', a: 'Near hospitals, schools, heritage sites, parks, cremation grounds, police stations, water bodies, central medians, within 100m of highway junctions.' },
];

const EXEMPTIONS = [
  'Own shop/business name board on own building',
  'Newspaper, TV, and radio ads',
  'Election-related displays',
  'Displays inside private property not visible from public road',
  'Government gazette notices',
];

export const DisplayDevicesSection: React.FC = () => {
  const { ref, isRevealed } = useScrollReveal({ threshold: 0.05 });
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  return (
    <section
      id="display-devices"
      ref={ref as React.RefObject<HTMLElement>}
      className={`scroll-reveal py-10 lg:py-14 bg-gradient-to-b from-[#FFF9F5] to-white border-t border-[#E0E0E0] scroll-mt-16 ${isRevealed ? 'revealed' : ''}`}
    >
      <div className="reveal-content max-w-7xl mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 bg-[#E66A1A]/10 text-[#E66A1A] text-xs font-bold uppercase tracking-wider px-4 py-1.5 rounded-full mb-3">
            <i className="ri-tv-2-line" /> GO Ms.No.253
          </div>
          <h2 className="text-2xl lg:text-3xl font-extrabold text-[#222]">Display Devices — Outdoor Advertising</h2>
          <p className="mt-2 text-sm text-gray-500 max-w-2xl mx-auto">
            The AP Regulation and Control of Display Devices Rules, 2025 require all outdoor advertisements to be licensed and permitted through Urban Local Bodies.
          </p>
        </div>

        {/* Quick Facts */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-10">
          {[
            { icon: 'ri-time-line', value: '3 Years', label: 'License Validity' },
            { icon: 'ri-ruler-line', value: '50+ sq.ft', label: 'Structural Cert. Required' },
            { icon: 'ri-money-rupee-circle-line', value: '₹25K–₹1L', label: 'Security Deposit' },
            { icon: 'ri-building-line', value: '110+ ULBs', label: 'Across AP' },
          ].map((f, i) => (
            <div key={i} className="bg-white rounded-xl border border-[#EAEAEA] p-4 text-center hover:shadow-md transition-shadow">
              <i className={`${f.icon} text-xl text-[#E66A1A]`} />
              <p className="text-lg font-extrabold text-[#222] mt-1">{f.value}</p>
              <p className="text-[11px] text-gray-500">{f.label}</p>
            </div>
          ))}
        </div>

        {/* Process Steps */}
        <div className="mb-10">
          <h3 className="flex items-center gap-2 text-base font-bold text-[#222] mb-4">
            <div className="w-1 h-5 bg-[#E66A1A] rounded-sm" /> How to Apply
          </h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
            {PROCESS_STEPS.map((s) => (
              <div key={s.step} className="bg-white rounded-xl border border-[#EAEAEA] p-4 flex gap-3 hover:shadow-md hover:border-[#E66A1A]/40 transition-all">
                <div className="w-8 h-8 rounded-full bg-[#E66A1A] text-white flex items-center justify-center text-sm font-bold flex-shrink-0">{s.step}</div>
                <div>
                  <h4 className="text-sm font-semibold text-[#222]">{s.title}</h4>
                  <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Device Types Table */}
        <div className="mb-10">
          <h3 className="flex items-center gap-2 text-base font-bold text-[#222] mb-4">
            <div className="w-1 h-5 bg-[#E66A1A] rounded-sm" /> Device Types & Requirements
          </h3>
          <div className="overflow-x-auto rounded-xl border border-[#EAEAEA]">
            <table className="w-full text-sm bg-white">
              <thead>
                <tr className="bg-[#FFF2E8]">
                  <th className="px-4 py-2.5 text-left font-bold text-xs uppercase text-[#9A3412]">Type</th>
                  <th className="px-4 py-2.5 text-left font-bold text-xs uppercase text-[#9A3412]">Examples</th>
                  <th className="px-4 py-2.5 text-center font-bold text-xs uppercase text-[#9A3412]">License</th>
                  <th className="px-4 py-2.5 text-center font-bold text-xs uppercase text-[#9A3412]">Structural</th>
                  <th className="px-4 py-2.5 text-center font-bold text-xs uppercase text-[#9A3412]">Insurance</th>
                  <th className="px-4 py-2.5 text-center font-bold text-xs uppercase text-[#9A3412]">Validity</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {DEVICE_TYPES.map((d, i) => (
                  <tr key={i} className="hover:bg-[#FFF9F5]">
                    <td className="px-4 py-2.5 font-semibold text-[#222]">{d.type}</td>
                    <td className="px-4 py-2.5 text-gray-600 text-xs">{d.examples}</td>
                    <td className="px-4 py-2.5 text-center">
                      {d.license ? <i className="ri-check-line text-green-600 font-bold" /> : <i className="ri-close-line text-gray-400" />}
                    </td>
                    <td className="px-4 py-2.5 text-center text-xs text-gray-600">{d.structural}</td>
                    <td className="px-4 py-2.5 text-center">
                      {d.insurance ? <i className="ri-check-line text-green-600 font-bold" /> : <i className="ri-close-line text-gray-400" />}
                    </td>
                    <td className="px-4 py-2.5 text-center text-xs font-semibold text-[#E66A1A]">{d.validity}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Exemptions */}
        <div className="mb-10">
          <h3 className="flex items-center gap-2 text-base font-bold text-[#222] mb-4">
            <div className="w-1 h-5 bg-[#1E8E3E] rounded-sm" /> Who is Exempt?
          </h3>
          <div className="bg-white rounded-xl border border-[#EAEAEA] p-5">
            <div className="grid sm:grid-cols-2 gap-2">
              {EXEMPTIONS.map((e, i) => (
                <div key={i} className="flex items-start gap-2 text-sm text-gray-700">
                  <i className="ri-check-double-line text-[#1E8E3E] mt-0.5 flex-shrink-0" />
                  <span>{e}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* FAQ Accordion */}
        <div className="mb-8">
          <h3 className="flex items-center gap-2 text-base font-bold text-[#222] mb-4">
            <div className="w-1 h-5 bg-[#E66A1A] rounded-sm" /> Frequently Asked Questions
          </h3>
          <div className="space-y-2">
            {FAQS.map((faq, i) => (
              <div key={i} className="bg-white rounded-xl border border-[#EAEAEA] overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full px-5 py-3.5 flex items-center justify-between text-left hover:bg-[#FFF9F5] transition-colors"
                >
                  <span className="flex items-center gap-2.5 text-sm font-semibold text-[#222]">
                    <span className="text-[11px] bg-[#FFF2E8] text-[#E66A1A] font-bold w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0">
                      {i + 1}
                    </span>
                    {faq.q}
                  </span>
                  <i className={`ri-arrow-down-s-line text-lg text-gray-400 transition-transform duration-200 ${openFaq === i ? 'rotate-180' : ''}`} />
                </button>
                {openFaq === i && (
                  <div className="px-5 pb-4 text-sm text-gray-600 leading-relaxed border-t border-gray-100 pt-3 ml-8">
                    {faq.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="bg-gradient-to-r from-[#E66A1A] to-[#D4590F] rounded-2xl p-6 lg:p-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-white">
          <div>
            <h3 className="text-lg font-extrabold">Ready to Apply?</h3>
            <p className="text-sm text-white/80 mt-1">Register as a Displayer and manage your outdoor advertising online.</p>
          </div>
          <a
            href="/app/maud-erp/display-devices"
            className="inline-flex items-center gap-2 bg-white text-[#E66A1A] font-bold text-sm px-6 py-2.5 rounded-lg hover:bg-orange-50 transition-colors flex-shrink-0"
          >
            <i className="ri-login-box-line" /> Go to ERP Portal
          </a>
        </div>
      </div>
    </section>
  );
};
